import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { updateCustomerLanguagePreferenceFromAuth } from 'api/auth'
import setLanguageCookie from 'cookies/setLanguageCookie'
import { LabRemoteConfigKeys } from 'constants/lab'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import { useAppSelector } from 'hooks/reduxHooks'
import { getVisibleLanguageOptions } from 'i18n/supportedLanguageOptions'
import { SUPPORTED_LOCALE_OPTIONS } from 'i18n/supportedLocales'
import { replaceLocaleInUrl } from 'i18n/util/localeUrl'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { reportClientError } from 'services/errorReportingService'
import { selectLoggedIn } from 'selectors/accountSelectors'

interface Props {
  onLanguageSelect?: React.MouseEventHandler<HTMLButtonElement>
}

function NavBarLanguageItems(props: Props) {
  const { onLanguageSelect } = props
  const locale = useAppSelector(state => state.geo.locale)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const languagesRemoteConfig = useLabRemoteConfig(LabRemoteConfigKeys.languages)
  const languageOptions = useMemo(() => {
    return getVisibleLanguageOptions(languagesRemoteConfig)
  }, [languagesRemoteConfig])
  const fallbackLanguageOptions = SUPPORTED_LOCALE_OPTIONS

  const currentLanguage = languageOptions.find(lang => lang.locale === locale) ?? fallbackLanguageOptions[0]

  const onLanguageClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const selectedLocale = e.currentTarget.dataset.locale
    const selectedLanguage = languageOptions.find(language => language.locale === selectedLocale)
    if (selectedLanguage) {
      setLanguageCookie(selectedLanguage.locale)
    }

    if (selectedLanguage && selectedLanguage.locale !== currentLanguage.locale) {
      if (isLoggedIn) {
        updateCustomerLanguagePreferenceFromAuth(selectedLanguage.locale).catch(reportClientError)
      }

      const url = new URL(window.location.href)
      url.searchParams.delete('lang')
      window.location.href = replaceLocaleInUrl(`${url.pathname}${url.search}${url.hash}`, selectedLanguage.locale)
    }
    onLanguageSelect?.(e)
  }, [currentLanguage.locale, isLoggedIn, languageOptions, onLanguageSelect])

  return <>
    {languageOptions.map(language => <ClickableListItem
      size="medium"
      key={language.locale}
      endIcon={currentLanguage.locale === language.locale ? <LineCheckIcon /> : undefined}
      title={language.nativeName}
      data-locale={language.locale}
      onClick={onLanguageClick}
      selected={currentLanguage.locale === language.locale}
    />)}
  </>
}

export default React.memo(NavBarLanguageItems)
